// Contrôle du RYTHME d'une animation : l'élève doit avoir le temps de lire et
// d'écouter. Pur, sur le document compilé (calendrier réel des étapes).
//
//   - une ou deux apparitions par phrase de la voix, pas plus ;
//   - au moins 0,8 s entre deux apparitions successives (montrées ensemble,
//     elles n'en font qu'une) ;
//   - un CALCUL (transformation de formule) par phrase : la voix dit chaque
//     étape pendant qu'elle s'affiche (le calendrier laisse ensuite le temps
//     de la lire avant la phrase suivante) ;
//   - une scène dure au moins quelques phrases.
//
// Une question (porte) remet les compteurs à zéro : le temps de réflexion de
// l'élève sépare ce qui la précède de ce qui la suit.

import type { CompiledAnimation } from './timeline';
import type { JrafAction } from './schema/steps';

/** Actions qui changent ce que voit l'élève (une « apparition »). */
const VISUAL: ReadonlySet<JrafAction> = new Set([
  'object.show',
  'object.move',
  'object.scale',
  'object.rotate',
  'formula.transform',
  'label.set',
  'value.set',
]);

/** Étapes démarrées à moins de cet écart : montrées ensemble (une seule apparition). */
export const SIMULTANEOUS_MS = 120;
/** Écart minimal entre deux apparitions successives. */
export const MIN_GAP_MS = 800;
/** Apparitions au plus pendant une phrase. */
export const MAX_EVENTS_PER_SENTENCE = 2;
/** Durée minimale d'une scène (narration), en ms. */
export const MIN_SCENE_MS = 5000;

/** Transformations de formule au plus pendant une phrase. */
export const MAX_CALCS_PER_SENTENCE = 1;

export type PacingIssueCode = 'pace_crowded' | 'pace_rushed' | 'pace_calc' | 'scene_short';

export interface PacingIssue {
  code: PacingIssueCode;
  sceneIndex: number;
  /** index de l'étape en cause (absent : toute la scène) */
  stepIndex?: number;
  message: string;
}

function seconds(ms: number): string {
  return (Math.round(ms / 100) / 10).toString().replace('.', ',');
}

export function checkPacing(anim: CompiledAnimation): PacingIssue[] {
  const out: PacingIssue[] = [];
  anim.scenes.forEach((scene, si) => {
    const where = `scène « ${scene.scene.title} »`;
    const { narrationMs } = scene.schedule;
    if (narrationMs < MIN_SCENE_MS)
      out.push({
        code: 'scene_short',
        sceneIndex: si,
        message: `${where} : environ ${seconds(narrationMs)} s de narration, trop rapide — 3 à 5 phrases par scène`,
      });

    // apparitions : étapes visuelles regroupées quand elles démarrent ensemble
    const visual = scene.schedule.steps
      .filter((s) => !s.gate && VISUAL.has(s.action))
      .sort((a, b) => a.startMs - b.startMs || a.index - b.index);
    const gateStarts = scene.schedule.steps.filter((s) => s.gate).map((s) => s.startMs);
    /** tranche de la scène : combien de questions avant cet instant */
    const segmentOf = (t: number) => gateStarts.filter((g) => g < t).length;
    const events: { startMs: number; stepIndex: number; stepId: string; segment: number }[] = [];
    for (const s of visual) {
      const last = events[events.length - 1];
      const segment = segmentOf(s.startMs);
      if (last && last.segment === segment && s.startMs - last.startMs < SIMULTANEOUS_MS) continue;
      events.push({ startMs: s.startMs, stepIndex: s.index, stepId: s.id, segment });
    }

    // trop d'apparitions pendant une même phrase
    const starts = scene.sentenceStartsMs;
    const perSentence = new Map<string, typeof events>();
    const sentenceOf = new Map<string, number>();
    for (const e of events) {
      let k = 0;
      starts.forEach((s, i) => {
        if (s <= e.startMs) k = i;
      });
      const key = `${e.segment}|${k}`;
      sentenceOf.set(key, k);
      perSentence.set(key, [...(perSentence.get(key) ?? []), e]);
    }
    for (const [key, list] of perSentence) {
      const k = sentenceOf.get(key)!;
      if (list.length <= MAX_EVENTS_PER_SENTENCE) continue;
      out.push({
        code: 'pace_crowded',
        sceneIndex: si,
        stepIndex: list[MAX_EVENTS_PER_SENTENCE]!.stepIndex,
        message: `${where}, phrase ${k + 1} : ${list.length} apparitions (${list.map((e) => `« ${e.stepId} »`).join(', ')}) — une ou deux par phrase ; répartis-les sur les phrases (atSentence) ou montre-les ensemble`,
      });
    }

    // plusieurs calculs dans une même phrase
    const calcs = new Map<string, { k: number; ids: string[]; stepIndex: number }>();
    for (const st of scene.schedule.steps) {
      if (st.gate || st.action !== 'formula.transform') continue;
      let k = 0;
      starts.forEach((s, i) => {
        if (s <= st.startMs) k = i;
      });
      const key = `${segmentOf(st.startMs)}|${k}`;
      const cur = calcs.get(key) ?? { k, ids: [], stepIndex: st.index };
      cur.ids.push(st.id);
      if (cur.ids.length === MAX_CALCS_PER_SENTENCE + 1) cur.stepIndex = st.index;
      calcs.set(key, cur);
    }
    for (const { k, ids, stepIndex } of calcs.values()) {
      if (ids.length <= MAX_CALCS_PER_SENTENCE) continue;
      out.push({
        code: 'pace_calc',
        sceneIndex: si,
        stepIndex,
        message: `${where}, phrase ${k + 1} : ${ids.length} calculs (${ids.map((id) => `« ${id} »`).join(', ')}) — un calcul par phrase : découpe la narration pour que la voix dise chaque étape pendant qu'elle s'affiche`,
      });
    }

    // deux apparitions trop rapprochées
    for (let i = 1; i < events.length; i++) {
      if (events[i]!.segment !== events[i - 1]!.segment) continue; // une question entre les deux
      const gap = events[i]!.startMs - events[i - 1]!.startMs;
      if (gap >= MIN_GAP_MS) continue;
      out.push({
        code: 'pace_rushed',
        sceneIndex: si,
        stepIndex: events[i]!.stepIndex,
        message: `${where} : « ${events[i]!.stepId} » arrive ${seconds(gap)} s après « ${events[i - 1]!.stepId} » — laisse ${seconds(MIN_GAP_MS)} s au moins (atSentence, delayMs) ou montre-les ensemble (with)`,
      });
    }
  });
  return out;
}
